import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  bodyPartTemplateId: '',
  displayName: 'Pain',
  minimumScore: 0,
  maximumScore: 10,
  bestRequired: true,
  currentRequired: true,
  worstRequired: true,
  unchangedNoteLimit: 1,
  displayOrder: 0,
  active: true,
  adminNotes: '',
};

function normalizeTemplateToForm(template) {
  return {
    bodyPartTemplateId:
      template?.bodyPartTemplateId === null ||
      template?.bodyPartTemplateId === undefined
        ? ''
        : String(template.bodyPartTemplateId),
    displayName: template?.displayName || '',
    minimumScore:
      template?.minimumScore === null || template?.minimumScore === undefined
        ? 0
        : Number(template.minimumScore),
    maximumScore:
      template?.maximumScore === null || template?.maximumScore === undefined
        ? 10
        : Number(template.maximumScore),
    bestRequired: Boolean(template?.bestRequired),
    currentRequired: Boolean(template?.currentRequired),
    worstRequired: Boolean(template?.worstRequired),
    unchangedNoteLimit:
      template?.unchangedNoteLimit === null ||
      template?.unchangedNoteLimit === undefined
        ? 0
        : Number(template.unchangedNoteLimit),
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
    adminNotes: template?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function validateForm(form) {
  const errors = {};
  const bodyPartTemplateId = Number(form.bodyPartTemplateId);
  const displayName = String(form.displayName || '').trim();
  const minimumScore = Number(form.minimumScore);
  const maximumScore = Number(form.maximumScore);
  const unchangedNoteLimit = Number(form.unchangedNoteLimit);
  const displayOrder = Number(form.displayOrder);
  const adminNotes = String(form.adminNotes || '').trim();

  if (!Number.isInteger(bodyPartTemplateId) || bodyPartTemplateId <= 0) {
    errors.bodyPartTemplateId = 'Select a body part.';
  }

  if (!displayName) {
    errors.displayName = 'Pain display name is required.';
  } else if (displayName.length > 120) {
    errors.displayName = 'Pain display name cannot exceed 120 characters.';
  }

  if (!Number.isInteger(minimumScore) || minimumScore < 0) {
    errors.minimumScore = 'Minimum score must be a whole number of 0 or greater.';
  }

  if (!Number.isInteger(maximumScore) || maximumScore < 0) {
    errors.maximumScore = 'Maximum score must be a whole number of 0 or greater.';
  }

  if (
    !errors.minimumScore &&
    !errors.maximumScore &&
    minimumScore > maximumScore
  ) {
    errors.maximumScore = 'Maximum score must be greater than or equal to minimum score.';
  }

  if (!Number.isInteger(unchangedNoteLimit) || unchangedNoteLimit < 0) {
    errors.unchangedNoteLimit =
      'Unchanged-note limit must be a whole number of 0 or greater.';
  }

  if (!Number.isInteger(displayOrder) || displayOrder < 0) {
    errors.displayOrder = 'Display order must be a whole number of 0 or greater.';
  }

  if (adminNotes.length > 500) {
    errors.adminNotes = 'Admin notes cannot exceed 500 characters.';
  }

  return errors;
}

function buildRequest(form) {
  return {
    bodyPartTemplateId: Number(form.bodyPartTemplateId),
    displayName: String(form.displayName || '').trim(),
    minimumScore: Number(form.minimumScore),
    maximumScore: Number(form.maximumScore),
    bestRequired: Boolean(form.bestRequired),
    currentRequired: Boolean(form.currentRequired),
    worstRequired: Boolean(form.worstRequired),
    unchangedNoteLimit: Number(form.unchangedNoteLimit),
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
    adminNotes: String(form.adminNotes || '').trim() || null,
  };
}

export default function PainTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [bodyPartFilter, setBodyPartFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.painTemplateId);

  const bodyPartMap = useMemo(() => {
    const map = new Map();

    bodyParts.forEach((bodyPart) => {
      map.set(Number(bodyPart.bodyPartTemplateId), bodyPart);
    });

    return map;
  }, [bodyParts]);

  const sortedTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...templates]
      .filter((template) => {
        if (statusFilter === 'ACTIVE' && !template.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && template.active) {
          return false;
        }

        if (
          bodyPartFilter !== 'ALL' &&
          String(template.bodyPartTemplateId) !== String(bodyPartFilter)
        ) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));

        return [
          template.painTemplateId,
          template.displayName,
          template.adminNotes,
          bodyPart?.bodyPartCode,
          bodyPart?.displayName,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstBodyPart = bodyPartMap.get(Number(first.bodyPartTemplateId));
        const secondBodyPart = bodyPartMap.get(Number(second.bodyPartTemplateId));

        const bodyPartComparison = String(firstBodyPart?.displayName || '').localeCompare(
          String(secondBodyPart?.displayName || '')
        );

        if (bodyPartComparison !== 0) {
          return bodyPartComparison;
        }

        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [templates, bodyPartMap, searchTerm, statusFilter, bodyPartFilter]);

  const activeCount = useMemo(
    () => templates.filter((template) => template.active).length,
    [templates]
  );

  useEffect(() => {
    loadData();
  }, []);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const [templateData, bodyPartData] = await Promise.all([
        clinicalTemplateAdminService.getAllPainTemplates(),
        clinicalTemplateAdminService.getAllBodyPartTemplates(),
      ]);

      setTemplates(Array.isArray(templateData) ? templateData : []);
      setBodyParts(Array.isArray(bodyPartData) ? bodyPartData : []);
    } catch (error) {
      setTemplates([]);
      setBodyParts([]);
      setLoadError(error?.message || 'Pain-template configuration could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updatePainTemplate(
          editingTemplate.painTemplateId,
          request
        );
        showSuccess('Pain template updated.');
      } else {
        await clinicalTemplateAdminService.createPainTemplate(request);
        showSuccess('Pain template created.');
      }

      resetForm();
      await loadData();
    } catch (error) {
      setActionError(error?.message || 'Pain template could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.active;
    const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));

    if (nextActive && bodyPart && !bodyPart.active) {
      setActionError(
        `"${bodyPart.displayName}" is inactive. Activate the body part before activating this pain template.`
      );
      return;
    }

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate the pain template for "${bodyPart?.displayName || template.displayName}"? Historical notes will keep their existing template reference.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(template.painTemplateId);
      setActionError('');

      await clinicalTemplateAdminService.setPainTemplateActive(
        template.painTemplateId,
        nextActive
      );

      showSuccess(`Pain template ${nextActive ? 'activated' : 'deactivated'}.`);

      if (editingTemplate?.painTemplateId === template.painTemplateId) {
        setForm((current) => ({
          ...current,
          active: nextActive,
        }));
        setEditingTemplate((current) => ({
          ...current,
          active: nextActive,
        }));
      }

      await loadData();
    } catch (error) {
      setActionError(
        error?.message ||
          `Pain template could not be ${nextActive ? 'activated' : 'deactivated'}.`
      );
    } finally {
      setActiveActionId(null);
    }
  };

  const getBodyPartLabel = (bodyPartTemplateId) => {
    const bodyPart = bodyPartMap.get(Number(bodyPartTemplateId));

    if (!bodyPart) {
      return `Body Part #${bodyPartTemplateId}`;
    }

    return `${bodyPart.displayName} (${bodyPart.bodyPartCode})`;
  };

  return (
    <div className="pain-template-admin">
      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">PAIN CONFIGURATION</span>
            <h2>{isEditing ? 'Edit Pain Template' : 'Create Pain Template'}</h2>
            <p>
              Configure the pain scale used for a single body part. The backend
              permits only one active pain template per body part.
            </p>
          </div>

          {isEditing && (
            <button
              type="button"
              className="clinical-template-secondary-button"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          )}
        </div>

        {actionError && (
          <div className="clinical-template-message error" role="alert">
            {actionError}
          </div>
        )}

        {success && (
          <div className="clinical-template-message success" role="status">
            {success}
          </div>
        )}

        <form className="pain-template-form" onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Body Part
              <select
                value={form.bodyPartTemplateId}
                onChange={(event) =>
                  updateFormField('bodyPartTemplateId', event.target.value)
                }
                disabled={isEditing || saving}
              >
                <option value="">Select body part</option>
                {bodyParts.map((bodyPart) => (
                  <option
                    key={bodyPart.bodyPartTemplateId}
                    value={bodyPart.bodyPartTemplateId}
                  >
                    {bodyPart.displayName} ({bodyPart.bodyPartCode})
                    {!bodyPart.active ? ' - Inactive' : ''}
                  </option>
                ))}
              </select>
              {formErrors.bodyPartTemplateId && (
                <span className="clinical-template-field-error">
                  {formErrors.bodyPartTemplateId}
                </span>
              )}
              {isEditing && (
                <small>Body-part assignment cannot be changed after creation.</small>
              )}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={120}
                disabled={saving}
                placeholder="Pain"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>

            <label>
              Minimum Pain Score
              <input
                type="number"
                min="0"
                step="1"
                value={form.minimumScore}
                onChange={(event) =>
                  updateFormField('minimumScore', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.minimumScore && (
                <span className="clinical-template-field-error">
                  {formErrors.minimumScore}
                </span>
              )}
            </label>

            <label>
              Maximum Pain Score
              <input
                type="number"
                min="0"
                step="1"
                value={form.maximumScore}
                onChange={(event) =>
                  updateFormField('maximumScore', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.maximumScore && (
                <span className="clinical-template-field-error">
                  {formErrors.maximumScore}
                </span>
              )}
            </label>

            <label>
              Unchanged-Note Warning Limit
              <input
                type="number"
                min="0"
                step="1"
                value={form.unchangedNoteLimit}
                onChange={(event) =>
                  updateFormField('unchangedNoteLimit', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.unchangedNoteLimit && (
                <span className="clinical-template-field-error">
                  {formErrors.unchangedNoteLimit}
                </span>
              )}
              <small>0 disables repeated-value warnings.</small>
            </label>
          </div>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.bestRequired}
                onChange={(event) =>
                  updateFormField('bestRequired', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Best Pain Required</strong>
                <small>Require a Best pain score before final validation.</small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.currentRequired}
                onChange={(event) =>
                  updateFormField('currentRequired', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Current Pain Required</strong>
                <small>Require a Current pain score before final validation.</small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.worstRequired}
                onChange={(event) =>
                  updateFormField('worstRequired', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Worst Pain Required</strong>
                <small>Require a Worst pain score before final validation.</small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  updateFormField('active', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>
                  Active templates may be used by new clinical notes. Only one
                  active pain template is allowed per body part.
                </small>
              </span>
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              value={form.adminNotes}
              onChange={(event) =>
                updateFormField('adminNotes', event.target.value)
              }
              maxLength={500}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Save Pain Template'
                  : 'Create Pain Template'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-editor-panel clinical-template-list-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">CONFIGURED PAIN TEMPLATES</span>
            <h2>Pain Templates</h2>
            <p>
              {templates.length} total configuration{templates.length === 1 ? '' : 's'} ·{' '}
              {activeCount} active
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={loadData}
            disabled={loading || saving || activeActionId !== null}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        <div
          className="clinical-template-list-toolbar"
          style={{ gridTemplateColumns: 'minmax(220px, 1fr) 220px 260px' }}
        >
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search pain templates..."
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>

          <select
            value={bodyPartFilter}
            onChange={(event) => setBodyPartFilter(event.target.value)}
          >
            <option value="ALL">All body parts</option>
            {bodyParts.map((bodyPart) => (
              <option
                key={bodyPart.bodyPartTemplateId}
                value={bodyPart.bodyPartTemplateId}
              >
                {bodyPart.displayName} ({bodyPart.bodyPartCode})
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="clinical-template-empty-state">Loading pain templates...</div>
        ) : sortedTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">
            No pain templates match the current filters.
          </div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Body Part</th>
                  <th>Name</th>
                  <th>Scale</th>
                  <th>Required Scores</th>
                  <th>Repeat Limit</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Audit</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedTemplates.map((template) => (
                  <tr key={template.painTemplateId}>
                    <td>
                      <strong>{getBodyPartLabel(template.bodyPartTemplateId)}</strong>
                      <small>Template ID: {template.painTemplateId}</small>
                    </td>

                    <td>
                      <strong>{template.displayName || 'Pain'}</strong>
                      {template.adminNotes && <small>{template.adminNotes}</small>}
                    </td>

                    <td>
                      {template.minimumScore} - {template.maximumScore}
                    </td>

                    <td>
                      <small>Best: {template.bestRequired ? 'Yes' : 'No'}</small>
                      <small>Current: {template.currentRequired ? 'Yes' : 'No'}</small>
                      <small>Worst: {template.worstRequired ? 'Yes' : 'No'}</small>
                    </td>

                    <td>{template.unchangedNoteLimit}</td>
                    <td>{template.displayOrder}</td>

                    <td>
                      <strong>{template.active ? 'Active' : 'Inactive'}</strong>
                    </td>

                    <td>
                      <small>Created by: {template.createdByStaffId ?? 'N/A'}</small>
                      <small>Updated by: {template.updatedByStaffId ?? 'N/A'}</small>
                      <small>Updated: {formatDateTime(template.updatedAt)}</small>
                    </td>

                    <td>
                      <div className="clinical-template-row-actions">
                        <button
                          type="button"
                          onClick={() => beginEdit(template)}
                          disabled={activeActionId !== null || saving}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className={
                            template.active
                              ? 'clinical-template-danger-button'
                              : 'clinical-template-activate-button'
                          }
                          onClick={() => toggleActive(template)}
                          disabled={
                            activeActionId === template.painTemplateId || saving
                          }
                        >
                          {activeActionId === template.painTemplateId
                            ? 'Saving...'
                            : template.active
                              ? 'Deactivate'
                              : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
